import React, { useState, useMemo, useEffect } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import SmartLogo from "@/components/SmartLogo";
import { User, Plus, LogOut, Settings, History, Heart, Shield, Wrench, Store, DollarSign, Check, Briefcase, Download, Info, Newspaper, MessageCircle, Bell } from "lucide-react";
import { useUnreadNotifications } from "@/hooks/useUnreadNotifications";
import { usePwaEnabled } from "@/hooks/usePwaEnabled";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useProfile } from "@/hooks/useProfile";
import { useListingSaves } from "@/hooks/useListingSaves";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useToast } from "@/hooks/use-toast";
import type { Currency } from "@/contexts/CurrencyContext";
import HelicopterSilhouetteIcon from "@/components/icons/HelicopterSilhouetteIcon";
import engineTurbineBase from "@/assets/engine-turbine-base.png";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/components/AuthProvider";
import { useAdminAccess } from "@/hooks/useAdminAccess";
import { useOptimizedConversations as useConversations } from '@/hooks/useOptimizedConversations';
import NotificationCenter from "@/components/notifications/NotificationCenter";
import NavigationSearch from "@/components/NavigationSearch";
import { MessagesPopover } from "@/components/messaging/MessagesPopover";
import { HelpMenu } from "@/components/tutorial/HelpMenu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import chopperSwapLogo from "@/assets/chopperswap-logo-no-bg.png";
import { cn } from "@/lib/utils";

const currencyOptions = [
  { value: 'USD' as const, symbol: '$', label: 'USD' },
  { value: 'EUR' as const, symbol: '€', label: 'EUR' },
  { value: 'GBP' as const, symbol: '£', label: 'GBP' },
  { value: 'CAD' as const, symbol: 'CA$', label: 'CAD' },
  { value: 'AUD' as const, symbol: 'A$', label: 'AUD' },
];


interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

const Navigation = React.memo(() => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, signOut } = useAuth();
  const { isAdmin } = useAdminAccess();
  const { conversations } = useConversations();
  const { profile } = useProfile();
  const { savesCount } = useListingSaves();
  const { currency: currentCurrency, setCurrency } = useCurrency();
  const { toast } = useToast();
  const { unreadCount: unreadNotifications } = useUnreadNotifications();
  const unreadMessages = useMemo(
    () => conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0),
    [conversations]
  );
  const pwaEnabled = usePwaEnabled();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  // Listen for beforeinstallprompt to show install button
  useEffect(() => {
    if (!pwaEnabled) return;
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [pwaEnabled]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    } catch (error) {
      console.error('Install prompt error:', error);
    }
  };

  const favoritesCount = savesCount;

  // Handle currency change
  const handleCurrencyChange = (value: Currency) => {
    setCurrency(value);
    toast({ title: 'Currency updated', description: `Prices will display in ${value}` });
  };

  // Detect active asset type from URL (pathname or query param) - use startsWith to handle trailing slashes and refreshes
  const currentAsset = useMemo(() => {
    if (location.pathname.startsWith('/engines')) return 'engine';
    if (location.pathname.startsWith('/parts-tools')) return 'parts';
    if (location.pathname.startsWith('/jobs')) return 'jobs';
    if (location.pathname.startsWith('/services')) return 'services';
    const assetParam = searchParams.get('asset');
    if (assetParam === 'engine') return 'engine';
    if (assetParam === 'parts') return 'parts';
    if (assetParam === 'jobs') return 'jobs';
    if (assetParam === 'services') return 'services';
    return 'helicopter';
  }, [location.pathname, searchParams]);

  // Detect if we're on a landing page
  const isLandingPage = location.pathname === '/' || location.pathname.startsWith('/engines') || location.pathname.startsWith('/parts-tools') || location.pathname.startsWith('/jobs') || location.pathname.startsWith('/services');

  // Detect if we're in an engine or parts context for List button
  const isEngineContext = useMemo(() => {
    const path = location.pathname;
    const assetParam = searchParams.get('asset');
    return path.includes('engine') || assetParam === 'engine';
  }, [location, searchParams]);

  const isPartsContext = useMemo(() => {
    const path = location.pathname;
    const assetParam = searchParams.get('asset');
    return path.includes('parts') || assetParam === 'parts';
  }, [location, searchParams]);

  const isJobsContext = useMemo(() => {
    const path = location.pathname;
    const assetParam = searchParams.get('asset');
    return path.includes('jobs') || assetParam === 'jobs';
  }, [location, searchParams]);

  const isServicesContext = useMemo(() => {
    const path = location.pathname;
    const assetParam = searchParams.get('asset');
    return path.includes('services') || assetParam === 'services';
  }, [location, searchParams]);

  const engineListingRoute = '/create-listing-engine';
  const partsListingRoute = '/create-listing-parts';
  const jobsListingRoute = '/create-listing-jobs?type=job_post';
  const servicesListingRoute = '/create-listing-services';

  return (
    <nav data-tutorial-id="navigation-header" className="fixed top-0 left-0 right-0 z-50 bg-white/60 dark:bg-background/80 backdrop-blur-xl border-b border-white/40 dark:border-white/10 shadow-sm hidden lg:block h-[var(--nav-height-desktop)] overflow-visible">
      {/* Left: Logo + Join Button - Edge aligned */}
      <div className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 flex items-center gap-3">
        <Link to="/" className="flex items-center flex-shrink-0 h-[calc(var(--nav-height-desktop)-1rem)]">
          <img
            src={chopperSwapLogo}
            alt="ChopperSwap Logo"
            className="h-full w-auto max-h-20 object-contain transition-all duration-300 hover:scale-105 dark:brightness-0 dark:invert"
          />
        </Link>
        {/* Join Button for non-authenticated users */}
        {!user && (
          <Link to="/auth?mode=signup">
            <Button
              variant="default"
              size="sm"
              className="rounded-full px-5"
            >
              <Plus className="w-4 h-4 mr-1" />
              Join
            </Button>
          </Link>
        )}
      </div>

      {/* Center: Single row with tabs and search */}
      <div className="absolute left-[200px] lg:left-[280px] right-[200px] lg:right-[380px] top-1/2 -translate-y-1/2 flex items-center justify-center gap-2 lg:gap-4">
        {/* Asset Tabs */}
        <div className="flex items-center gap-1">
          <Link
            to="/"
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all duration-200",
              isLandingPage && currentAsset === 'helicopter'
                ? "bg-background text-foreground shadow-sm border border-border"
                : "text-foreground hover:text-foreground hover:bg-white/70 dark:hover:bg-muted/50"
            )}
          >
            <HelicopterSilhouetteIcon
              tone="nav"
              active={isLandingPage && currentAsset === 'helicopter'}
              className="w-5 h-5"
            />
            <span>Helicopters</span>
          </Link>
          <Link
            to="/engines"
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all duration-200",
              isLandingPage && currentAsset === 'engine'
                ? "bg-background text-foreground shadow-sm border border-border"
                : "text-foreground hover:text-foreground hover:bg-white/70 dark:hover:bg-muted/50"
            )}
          >
            <img
              src={engineTurbineBase}
              alt=""
              className={cn(
                "w-5 h-5 flex-shrink-0 object-contain brightness-0 dark:invert",
                isLandingPage && currentAsset === 'engine'
                  ? "opacity-90"
                  : "opacity-70"
              )}
            />
            <span>Engines</span>
          </Link>
          <Link
            to="/parts-tools"
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all duration-200",
              isLandingPage && currentAsset === 'parts'
                ? "bg-background text-foreground shadow-sm border border-border"
                : "text-foreground hover:text-foreground hover:bg-white/70 dark:hover:bg-muted/50"
            )}
          >
            <Wrench className="w-5 h-5 flex-shrink-0" />
            <span>Parts</span>
          </Link>
          <Link
            to="/jobs"
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all duration-200",
              isLandingPage && currentAsset === 'jobs'
                ? "bg-background text-foreground shadow-sm border border-border"
                : "text-foreground hover:text-foreground hover:bg-white/70 dark:hover:bg-muted/50"
            )}
          >
            <Briefcase className="w-5 h-5 flex-shrink-0" />
            <span>Jobs</span>
          </Link>
          <Link
            to="/services"
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all duration-200",
              isLandingPage && currentAsset === 'services'
                ? "bg-background text-foreground shadow-sm border border-border"
                : "text-foreground hover:text-foreground hover:bg-white/70 dark:hover:bg-muted/50"
            )}
          >
            <Settings className="w-5 h-5 flex-shrink-0" />
            <span>Services</span>
          </Link>
        </div>

        {/* Search Field */}
        <div data-tutorial-id="nav-search">
          <NavigationSearch />
        </div>

        {/* Marketplace Link */}
        <Link to={`/marketplace?asset=${currentAsset}`}>
          <Button
            variant="ghost"
            size="icon"
            className="hover:bg-muted/50 rounded-full"
            aria-label="Marketplace"
          >
            <Store className="w-4 h-4" />
          </Button>
        </Link>
      </div>

      {/* Right: User Actions - Edge aligned */}
      <div className="absolute right-2 lg:right-8 top-1/2 -translate-y-1/2 hidden lg:flex items-center gap-1 xl:gap-2 2xl:gap-4">
        {/* Install App - only when browser offers install */}
        {deferredPrompt && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleInstallClick}
            className="hover:bg-muted/50 px-2 gap-1 text-sm font-medium"
          >
            <Download className="w-4 h-4" />
            <span className="hidden xl:inline">Install</span>
          </Button>
        )}

        {/* Unified Profile & Options Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="hover:bg-muted/50 p-0 h-auto w-auto">
              {user ? (
                profile?.represent_as_company && profile?.company_name && profile?.company_logo_url ? (
                  <SmartLogo
                    src={profile.company_logo_url}
                    alt={profile.company_name || 'Company'}
                    variant="nav"
                    className="border-2 border-border/40"
                  />
                ) : (
                  <Avatar className="h-[clamp(2.5rem,3.5vw,3rem)] w-[clamp(2.5rem,3.5vw,3rem)] border-2 border-white/40 dark:border-white/20">
                    <AvatarImage src={profile?.avatar_url || ''} alt="Profile" />
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm lg:text-base font-semibold">
                      {profile?.represent_as_company && profile?.company_name
                        ? (profile?.company_name?.charAt(0).toUpperCase() || 'C')
                        : (user?.email?.charAt(0).toUpperCase() || 'U')}
                    </AvatarFallback>
                  </Avatar>
                )
              ) : (
                <Avatar className="h-[clamp(2.5rem,3.5vw,3rem)] w-[clamp(2.5rem,3.5vw,3rem)] border-2 border-white/40 dark:border-white/20">
                  <AvatarFallback className="bg-muted text-muted-foreground text-sm lg:text-base font-semibold">
                    <User className="w-5 h-5" />
                  </AvatarFallback>
                </Avatar>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            {user ? (
              <>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">Account</p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />

                {/* Flattened Profile Menu Items */}
                <DropdownMenuItem asChild>
                  <Link to="/messages">
                    <MessageCircle className="mr-2 h-4 w-4" />
                    <span>Messages</span>
                    {unreadMessages > 0 && (
                      <span className="ml-auto h-5 min-w-5 px-1 bg-primary text-primary-foreground text-[10px] rounded-full flex items-center justify-center font-bold">
                        {unreadMessages > 9 ? '9+' : unreadMessages}
                      </span>
                    )}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/profile?section=notifications">
                    <Bell className="mr-2 h-4 w-4" />
                    <span>Notifications</span>
                    {unreadNotifications > 0 && (
                      <span className="ml-auto h-5 min-w-5 px-1 bg-destructive text-destructive-foreground text-[10px] rounded-full flex items-center justify-center font-bold">
                        {unreadNotifications > 9 ? '9+' : unreadNotifications}
                      </span>
                    )}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/profile?section=overview">
                    <User className="mr-2 h-4 w-4" />
                    View Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/profile?section=dashboard">
                    <Settings className="mr-2 h-4 w-4" />
                    Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/profile?section=favorites">
                    <Heart className="mr-2 h-4 w-4" />
                    <span>Saved</span>
                    {favoritesCount > 0 && (
                      <span className="ml-auto h-5 min-w-5 px-1 bg-destructive text-destructive-foreground text-[10px] rounded-full flex items-center justify-center font-bold">
                        {favoritesCount > 9 ? '9+' : favoritesCount}
                      </span>
                    )}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/profile?section=transactions">
                    <History className="mr-2 h-4 w-4" />
                    Transaction History
                  </Link>
                </DropdownMenuItem>

                {isAdmin && (
                  <Link to="/admin">
                    <DropdownMenuItem>
                      <Shield className="mr-2 h-4 w-4" />
                      Admin Console
                    </DropdownMenuItem>
                  </Link>
                )}
              </>
            ) : (
              <>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">Welcome</p>
                    <p className="text-xs leading-none text-muted-foreground">
                      Guest User
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/auth">
                    <User className="mr-2 h-4 w-4" />
                    Sign In
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/auth?mode=signup">
                    <Plus className="mr-2 h-4 w-4" />
                    Sign Up
                  </Link>
                </DropdownMenuItem>
              </>
            )}

            <DropdownMenuSeparator />

            {/* Currency Submenu */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="cursor-pointer">
                <DollarSign className="mr-2 h-4 w-4" />
                <span>Currency ({currentCurrency})</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-40">
                <DropdownMenuLabel>Currency</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {currencyOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => handleCurrencyChange(option.value)}
                    className="flex items-center justify-between cursor-pointer"
                  >
                    <span>{option.symbol} {option.label}</span>
                    {currentCurrency === option.value && <Check className="w-4 h-4" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            {/* Help/Tutorials Submenu */}
            <HelpMenu isSubmenu={true} />

            {/* More Info Submenu */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="cursor-pointer">
                <Info className="mr-2 h-4 w-4" />
                <span>More Info</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/news" className="cursor-pointer">
                    <Newspaper className="mr-2 h-4 w-4" />
                    News
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            {user && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await signOut(); window.location.href = '/'; }}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* List Aircraft/Engines/Parts/Jobs Button */}
        {user && (
          <Link to={isServicesContext ? servicesListingRoute : isJobsContext ? jobsListingRoute : isPartsContext ? partsListingRoute : isEngineContext ? engineListingRoute : "/create-listing"}>
            <Button variant="default" className="px-5 lg:px-6">
              <Plus className="w-4 h-4 mr-2" />
              {isServicesContext ? 'List Service' : isJobsContext ? 'Post Job' : isPartsContext ? 'List Parts/Tools' : isEngineContext ? 'List Engines' : 'List Aircraft'}
            </Button>
          </Link>
        )}
      </div>
    </nav>
  );
});

export default Navigation;